/**
 * Shared hash-routing primitives for embeddable widgets.
 *
 * Embeddable widgets are mounted inside a host storefront page they do not
 * control, so they MUST NOT touch `location.pathname` / `location.search`
 * (those belong to the host server, which would 404 on shared links). Routing
 * lives entirely in the URL hash fragment: `#/{seg}/{seg}/{page}?query`.
 *
 * This module deliberately stays tiny and dependency-free: it owns only the
 * error-prone mechanics (codec + push/replace history discipline + listener
 * wiring). Each widget keeps its own route schema (which params exist, and
 * which are navigation vs. refinement). See the architecture decision doc:
 * `_bmad-output/planning-artifacts/architecture-widget-url-routing-2026-06-09`.
 */

/**
 * @param {boolean} debug
 * @param {string} label
 * @param {Record<string, unknown>} payload
 */
function logHashDebug(debug, label, payload) {
  if (!debug) return;
  // eslint-disable-next-line no-console
  console.log(`[AHRI hash] ${label}`, payload);
}

/**
 * Decode a `#/seg/seg/page?query` hash into its parts.
 *
 * @param {string} hash - Raw `location.hash` (may start with `#`).
 * @param {{ segmentCount: number, pages: Set<string> }} schema
 *   `segmentCount` = leading context segments before the page (e.g. 2 for
 *   `account/site`). `pages` = allow-list of valid page segments.
 * @param {{ debug?: boolean }} [options]
 * @returns {{ segments: string[], page: string, params: URLSearchParams } | null}
 *   `null` when the hash is empty, malformed, or the page is not allow-listed —
 *   callers should ignore it (the hash may belong to the host page).
 */
export function decodeHashRoute(hash, { segmentCount, pages }, options = {}) {
  const { debug = false } = options;
  const raw = String(hash || '').replace(/^#/, '');
  logHashDebug(debug, 'decodeHashRoute input', { hash, raw, segmentCount });

  if (!raw) {
    logHashDebug(debug, 'decodeHashRoute null', { reason: 'empty hash' });
    return null;
  }

  const [pathPart, queryPart = ''] = raw.split('?');
  const segments = pathPart
    .split('/')
    .filter(Boolean)
    .map((segment) => safeDecode(segment));
  const pageIndex = segmentCount;

  if (segments.length < pageIndex + 1) {
    logHashDebug(debug, 'decodeHashRoute null', {
      reason: 'not enough segments',
      segments,
      pageIndex,
    });
    return null;
  }

  const page = segments[pageIndex];
  if (pages && !pages.has(page)) {
    logHashDebug(debug, 'decodeHashRoute null', {
      reason: 'page not allow-listed',
      page,
      allowed: [...pages],
    });
    return null;
  }

  const decoded = {
    segments: segments.slice(0, segmentCount),
    page,
    params: new URLSearchParams(queryPart),
  };
  logHashDebug(debug, 'decodeHashRoute ok', {
    page: decoded.page,
    segments: decoded.segments,
    query: decoded.params.toString(),
  });
  return decoded;
}

/**
 * Encode parts into a `#page?query` (or `#seg/seg/page?query`) hash.
 *
 * The page sits immediately after `#` (no leading slash) so the readable
 * context lives in the host *path* before the `#` — e.g. `/ahri#shop?q=...`.
 * The widget only ever writes the hash; it must never touch the host path.
 *
 * @param {{ segments?: Array<string|number>, page: string, params?: URLSearchParams }} parts
 * @param {{ debug?: boolean }} [options]
 * @returns {string} A hash beginning with `#`.
 */
export function encodeHashRoute({ segments = [], page, params = null }, options = {}) {
  const { debug = false } = options;
  const path = [...segments, page]
    .map((segment) => encodeURIComponent(String(segment ?? '')))
    .join('/');
  const query = params ? params.toString() : '';
  const hash = `#${path}${query ? `?${query}` : ''}`;
  logHashDebug(debug, 'encodeHashRoute', { page, path, query, hash });
  return hash;
}

/**
 * Build a full same-origin URL for `history.pushState` / `replaceState`.
 * Keeps the host pathname and search intact; only the hash changes.
 *
 * @param {string} hash - Hash fragment from {@link encodeHashRoute}.
 * @param {{ debug?: boolean }} [options]
 * @returns {string}
 */
export function buildHistoryUrl(hash, options = {}) {
  const { debug = false } = options;
  if (typeof window === 'undefined') return hash;
  const url = `${window.location.pathname}${window.location.search}${hash}`;
  logHashDebug(debug, 'buildHistoryUrl', {
    hash,
    pathname: window.location.pathname,
    search: window.location.search,
    url,
  });
  return url;
}

/**
 * Commit a hash to history using push-vs-replace discipline.
 *
 * Navigation changes (a different view) push a new history entry; refinements
 * of the same view (search / filters / pagination) replace the current entry,
 * so the Back button steps between meaningful screens, not keystrokes.
 *
 * @param {string} nextHash - Target hash (from {@link encodeHashRoute}).
 * @param {string} navKey - Page-gated navigation identity of `nextHash`.
 * @param {{ current: string | null }} navKeyRef - Mutable ref holding the last
 *   committed navKey (e.g. a React `useRef`). Updated in place.
 * @param {{ debug?: boolean }} [options]
 * @returns {'push' | 'replace' | 'noop'}
 */
export function commitHashRoute(nextHash, navKey, navKeyRef, options = {}) {
  const { debug = false } = options;
  if (typeof window === 'undefined') return 'noop';

  const prevNavKey = navKeyRef.current;
  const currentHash = window.location.hash;
  const currentHref = window.location.href;

  if (currentHash === nextHash) {
    navKeyRef.current = navKey;
    logHashDebug(debug, 'commitHashRoute noop', { nextHash, navKey, currentHref });
    return 'noop';
  }

  const isInitial = navKeyRef.current === null;
  const isNavigation = !isInitial && navKey !== navKeyRef.current;
  navKeyRef.current = navKey;
  const nextUrl = buildHistoryUrl(nextHash, { debug });

  let action;
  if (isInitial) {
    // Always push on first write so Back has a meaningful target:
    // • Host already on #shop → back returns to the host's pre-sync hash.
    // • Host with no hash → back returns to the pre-widget page.
    window.history.pushState({ widgetRoute: true }, '', nextUrl);
    action = 'push';
  } else if (isNavigation) {
    window.history.pushState({ widgetRoute: true }, '', nextUrl);
    action = 'push';
  } else {
    window.history.replaceState({ widgetRoute: true }, '', nextUrl);
    action = 'replace';
  }

  logHashDebug(debug, 'commitHashRoute', {
    action,
    isInitial,
    isNavigation,
    prevNavKey,
    navKey,
    currentHash,
    nextHash,
    nextUrl,
    hrefAfter: window.location.href,
    historyLength: window.history.length,
  });

  return action;
}

/**
 * Subscribe to back/forward and hash changes.
 *
 * @param {() => void} handler - Invoked on `popstate` and `hashchange`.
 * @param {{ debug?: boolean }} [options]
 * @returns {() => void} Unsubscribe function.
 */
export function subscribeHashRoute(handler, options = {}) {
  const { debug = false } = options;
  if (typeof window === 'undefined') return () => {};

  const wrappedHandler = (event) => {
    logHashDebug(debug, 'subscribeHashRoute event', {
      type: event?.type ?? 'unknown',
      href: window.location.href,
      hash: window.location.hash,
      historyState: window.history.state,
      historyLength: window.history.length,
    });
    handler(event);
  };

  window.addEventListener('popstate', wrappedHandler);
  window.addEventListener('hashchange', wrappedHandler);
  logHashDebug(debug, 'subscribeHashRoute attached', { events: ['popstate', 'hashchange'] });

  return () => {
    window.removeEventListener('popstate', wrappedHandler);
    window.removeEventListener('hashchange', wrappedHandler);
    logHashDebug(debug, 'subscribeHashRoute detached', {});
  };
}

function safeDecode(segment) {
  try {
    return decodeURIComponent(segment);
  } catch {
    return segment;
  }
}
